import { useState, useEffect } from 'react'
import { Heart, MessageSquare, Zap } from 'lucide-react'
import { supabase } from '../../lib/supabase'

interface AgentStatsProps {
    agentId: string
    className?: string
}

interface Stats {
    total_likes: number
    total_conversations: number
    last_interaction_at: string | null
}

export function AgentStats({ agentId, className = '' }: AgentStatsProps) {
    const [stats, setStats] = useState<Stats | null>(null)
    const [isLoading, setIsLoading] = useState(true)

    useEffect(() => {
        let isMounted = true

        const fetchStats = async () => {
            // Validation: Skip if ID is 'RAG_SYSTEM' or not a UUID
            const isRagSystem = agentId === 'RAG_SYSTEM'
            const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(agentId)

            if (isRagSystem || !isUUID) {
                if (isMounted) {
                    setStats({ total_likes: 0, total_conversations: 0, last_interaction_at: null })
                    setIsLoading(false)
                }
                return
            }

            try {
                const { data, error } = await supabase
                    .from('agent_stats')
                    .select('total_likes, total_conversations, last_interaction_at')
                    .eq('agent_id', agentId)
                    .single()

                if (error && error.code !== 'PGRST116') { // Ignore "no rows" error
                    console.error('Error fetching agent stats:', error)
                }

                if (isMounted) {
                    setStats(data || { total_likes: 0, total_conversations: 0, last_interaction_at: null })
                    setIsLoading(false)
                }
            } catch (err) {
                console.error('Failed to load stats:', err)
                if (isMounted) setIsLoading(false)
            }
        }

        fetchStats()

        // Real-time subscription - ONLY if valid UUID
        let channel: any = null
        const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(agentId)

        if (isUUID) {
            channel = supabase
                .channel(`public:agent_stats:agent_id=eq.${agentId}`)
                .on(
                    'postgres_changes',
                    {
                        event: '*', // Listen for Insert, Update, Delete
                        schema: 'public',
                        table: 'agent_stats',
                        filter: `agent_id=eq.${agentId}`
                    },
                    (payload) => {
                        const newStats = payload.new as Stats
                        if (isMounted && newStats) {
                            setStats(prev => ({
                                ...prev,
                                // Handle potential partial updates or full replacements
                                total_likes: newStats.total_likes ?? prev?.total_likes ?? 0,
                                total_conversations: newStats.total_conversations ?? prev?.total_conversations ?? 0,
                                last_interaction_at: newStats.last_interaction_at
                            }))
                        }
                    }
                )
                .subscribe()
        }

        return () => {
            isMounted = false
            if (channel) supabase.removeChannel(channel)
        }
    }, [agentId])

    if (isLoading) {
        return <div className={`h-6 w-32 bg-gray-200 dark:bg-gray-800 rounded animate-pulse ${className}`} />
    }

    if (!stats) return null

    // Determine if "Trending" (Hot)
    // Example logic: Interaction in last 24h OR high like count. 
    // For now, let's keep it simple: Show badge if total_conversations > 10
    const isTrending = stats.total_conversations > 50

    return (
        <div className={`flex items-center gap-4 text-[15px] ${className}`}>
            {/* Likes */}
            <div className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400 group relative cursor-help">
                <Heart className="w-4 h-4 text-pink-500 fill-current" />
                <span className="font-medium text-gray-900 dark:text-gray-200">
                    {formatNumber(stats.total_likes)}
                </span>
                <span className="text-sm text-gray-500">Likes</span>

                {/* Tooltip */}
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                    Avaliações positivas totais
                </div>
            </div>

            {/* Conversations */}
            <div className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400 group relative cursor-help">
                <MessageSquare className="w-4 h-4 text-blue-500" />
                <span className="font-medium text-gray-900 dark:text-gray-200">
                    {formatNumber(stats.total_conversations)}
                </span>
                <span className="text-sm text-gray-500">Conversas</span>

                {/* Tooltip */}
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                    Total de conversas criadas
                </div>
            </div>

            {/* Special Badge: Trending */}
            {isTrending && (
                <div className="flex items-center gap-1 px-2 py-0.5 bg-orange-500/10 text-orange-600 dark:text-orange-400 rounded-full text-xs font-medium">
                    <Zap className="w-3 h-3 fill-current" />
                    <span>Em alta</span>
                </div>
            )}
        </div>
    )
}

function formatNumber(num: number): string {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M'
    if (num >= 1000) return (num / 1000).toFixed(1) + 'k'
    return num.toString()
}
